import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ScalesIcon as Scales, FloppyDiskIcon as FloppyDisk, TrashIcon as Trash } from "@phosphor-icons/react";

import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RowListSkeleton } from "@/components/SkeletonList";
import { formatApiError } from "@/lib/utils-app";

/**
 * Admin · Buyer's Bill of Rights — version history + acknowledgment
 * audit log. Mirrors AdminSellerStandards.jsx with a simplified
 * surface (no per-breed rules; the rights are universal).
 */
export default function AdminBuyerRights() {
  const [tab, setTab] = useState("versions");
  return (
    <AppShell>
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-crimson font-bold flex items-center gap-1.5 mb-2">
          <Scales size={12} weight="fill" /> Admin · Buyer's bill of rights
        </p>
        <h1 className="font-spectral text-4xl tracking-tight">Buyer's bill of rights</h1>
        <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
          Publish new versions of the Buyer's Bill of Rights and audit acknowledgment events
          collected at marketplace inquiry, escrow checkout, and the standalone public page.
        </p>
      </div>
      <div className="border-b border-[#E5E5E5] mb-6 flex gap-2 overflow-x-auto">
        {[
          ["versions", "Version history"],
          ["acks", "Acknowledgments"],
        ].map(([id, label]) => (
          <button
            key={id} type="button" onClick={() => setTab(id)}
            data-testid={`br-admin-tab-${id}`}
            className={`relative h-10 px-4 text-[13px] font-semibold transition-colors ${
              tab === id ? "text-[#0E0E0E]" : "text-[#6B6B6B] hover:text-[#0E0E0E]"
            }`}
          >
            {label}
            {tab === id && <span className="absolute bottom-0 inset-x-3 h-0.5 bg-[#C1272D]" />}
          </button>
        ))}
      </div>
      {tab === "versions" ? <VersionsTab /> : <AcksTab />}
    </AppShell>
  );
}

function VersionsTab() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ version: "", effective_date: "", version_notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/buyer-rights/versions");
      setVersions(r.data?.versions || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const publish = async () => {
    if (!draft.version.trim()) { toast.error("Version label required."); return; }
    setSubmitting(true);
    try {
      await api.post("/admin/buyer-rights/versions", {
        version: draft.version.trim(),
        effective_date: draft.effective_date.trim() || undefined,
        version_notes: draft.version_notes.trim() || undefined,
      });
      toast.success(`Version ${draft.version.trim()} published`);
      setDraft({ version: "", effective_date: "", version_notes: "" });
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const remove = async (v) => {
    if (!confirm(`Delete version ${v}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/buyer-rights/versions/${v}`);
      toast.success("Deleted");
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const setCurrent = async (v) => {
    try {
      await api.post("/admin/buyer-rights/set-current", { version: v });
      toast.success(`${v} is now current`);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section data-testid="br-versions-tab">
      <div className="rounded-2xl bg-[#FBF7EC] border border-[#E8D9A6] p-4 mb-6">
        <p className="text-[12px] text-[#8A6A00] mb-3 leading-relaxed">
          Publishing a new version preserves history. Existing acknowledgments retain their old version reference; buyers will be re-prompted at next checkout if you want them on the new version.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input data-testid="br-new-version-label" placeholder="Version label (e.g., v1.1)" value={draft.version} onChange={(e) => setDraft({ ...draft, version: e.target.value })} />
          <Input data-testid="br-new-version-date" placeholder="Effective date (YYYY-MM-DD)" value={draft.effective_date} onChange={(e) => setDraft({ ...draft, effective_date: e.target.value })} />
          <Button onClick={publish} disabled={submitting} data-testid="br-publish-version" className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
            <FloppyDisk size={14} weight="bold" className="mr-1.5" /> {submitting ? "Publishing…" : "Publish"}
          </Button>
        </div>
        <Textarea placeholder="Changelog note" value={draft.version_notes} onChange={(e) => setDraft({ ...draft, version_notes: e.target.value })} className="mt-3 min-h-[60px] text-[13px]" data-testid="br-new-version-notes" />
      </div>
      {loading ? <RowListSkeleton /> : (
        <ul className="space-y-2">
          {versions.map((v) => (
            <li key={v.version} data-testid={`br-version-${v.version}`} className="rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 flex items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-ink">
                  {v.version}
                  {v.is_current && <span className="ml-2 inline-flex items-center text-[10px] uppercase tracking-[0.14em] font-bold text-crimson bg-[#C1272D]/10 px-2 py-0.5 rounded-full">Current</span>}
                </p>
                <p className="text-[12px] text-[#6B6B6B]">Effective {v.effective_date} · published {v.created_at?.split("T")[0]}</p>
                {v.version_notes && <p className="text-[12px] text-[#6B6B6B] mt-1 italic">{v.version_notes}</p>}
              </div>
              <div className="flex items-center gap-2">
                {!v.is_current && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setCurrent(v.version)} data-testid={`br-set-current-${v.version}`} className="rounded-full">Make current</Button>
                    <Button size="sm" variant="outline" onClick={() => remove(v.version)} data-testid={`br-delete-${v.version}`} className="rounded-full border-[#C1272D] text-crimson"><Trash size={11} /></Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AcksTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/buyer-rights/acknowledgments")
      .then((r) => setRows(r.data?.acknowledgments || []))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <RowListSkeleton />;
  if (rows.length === 0) {
    return <p className="text-[#6B6B6B] text-sm" data-testid="br-acks-empty">No acknowledgments collected yet.</p>;
  }
  return (
    <ul className="space-y-2" data-testid="br-acks-list">
      {rows.map((a, i) => (
        <li key={`${a.user_id}-${a.acknowledged_at}-${i}`} data-testid={`br-ack-${i}`} className="rounded-xl border border-[#E5E5E5] bg-white px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-ink truncate">
              {a.user_email || a.user_id}
              <span className="ml-2 text-[10px] uppercase tracking-[0.14em] font-bold text-[#6B6B6B] bg-[#FAFAFA] px-2 py-0.5 rounded-full">{a.context}</span>
            </p>
            <p className="text-[12px] text-[#6B6B6B]">v{a.version} · {a.acknowledged_at?.split("T")[0]} {a.listing_id ? `· listing ${a.listing_id.slice(0,8)}…` : ""}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}
